import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from '../../services/authService';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const SignUp = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    // Validation
    if (formData?.password !== formData?.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData?.password?.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const { data, error: signUpError } = await authService?.signUp(
        formData?.email,
        formData?.password,
        formData?.fullName,
        formData?.phone
      );

      if (signUpError) {
        setError(signUpError?.message || 'Failed to create account');
        setLoading(false);
        return;
      }

      if (data?.user) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/signin');
        }, 3000);
      }
    } catch (err) {
      setError('An unexpected error occurred');
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-card rounded-lg shadow-sm border border-border p-8 text-center">
            <div className="w-16 h-16 bg-primary/10 text-primary rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="CheckCircle" size={32} />
            </div>
            <h2 className="font-headline font-bold text-2xl text-foreground mb-2">Account Created!</h2>
            <p className="font-body text-muted-foreground mb-4">
              A verification email has been sent to <strong>{formData?.email}</strong>. Please check your inbox and verify your email address.
            </p>
            <p className="font-body text-sm text-muted-foreground">
              Redirecting to sign in...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Icon name="Wrench" size={48} color="var(--color-primary)" />
          </div>
          <h1 className="font-headline font-bold text-3xl md:text-4xl text-foreground mb-2">
            Create Account
          </h1>
          <p className="font-body text-muted-foreground">
            Join meriodine for quality plumbing & electrical services
          </p>
        </div>

        <div className="bg-card rounded-lg shadow-sm border border-border p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Full Name"
              type="text"
              name="fullName"
              value={formData?.fullName}
              onChange={handleChange}
              placeholder="John Smith"
              required
            />

            <Input
              label="Phone Number"
              type="tel"
              name="phone"
              value={formData?.phone}
              onChange={handleChange}
              placeholder="082 123 4567"
              required
            />

            <Input
              label="Email Address"
              type="email"
              name="email"
              value={formData?.email}
              onChange={handleChange}
              placeholder="your.email@example.com"
              required
            />

            <Input
              label="Password"
              type="password"
              name="password"
              value={formData?.password}
              onChange={handleChange}
              placeholder="At least 6 characters"
              required
            />

            <Input
              label="Confirm Password"
              type="password"
              name="confirmPassword"
              value={formData?.confirmPassword}
              onChange={handleChange}
              placeholder="Re-enter password"
              required
            />

            {error && (
              <div className="bg-error/10 border border-error text-error rounded-lg p-3 text-sm">
                {error}
              </div>
            )}

            <Button
              type="submit"
              variant="default"
              size="lg"
              loading={loading}
              className="w-full"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="font-body text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link to="/signin" className="text-primary hover:underline font-medium">
                Sign In
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="font-body text-sm text-primary hover:underline">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignUp;